import { C_AMBER, C_TEAL, C_PURPLE, C_STEEL, C_CORAL, rgba } from '../utils/palette';
import { setupCanvas } from '../utils/canvas';

export const CHART_KEY = [
    'Each dot = one rider · X = age · Y = years skating',
    'Color = ranking tier: top 25% · top 50% · rest',
    'Tap a dot for rider name',
];

const STAGE_COLOR = {
    final:         rgba(C_CORAL,  0.8),
    semi:          rgba(C_AMBER,  0.8),
    quali:         rgba(C_TEAL,   0.8),
    pre:           rgba(C_PURPLE, 0.8),
    'not-ranked':  rgba(C_STEEL,  0.6),
};

/**
 * Scatter chart: age (x) vs years skating (y), coloured by stage.
 * @param {HTMLCanvasElement} canvas
 * @param {{ age: number, years: number, group: string, name?: string }[]} data
 * @param {(group: string) => string} stageOf  maps group name → stage key
 * @param {number} [clipPct]  clip x-axis to top N% of max age (100 = full range)
 * @param {HTMLElement} [tooltip]
 */
export function renderAgeScatter(canvas, data, stageOf, clipPct = 100, tooltip = null) {
    if (!canvas || !data.length) return;
    const pad = { top: 12, right: 12, bottom: 28, left: 28 };
    const w   = canvas.parentElement.clientWidth;
    const h   = 160;
    const ctx = setupCanvas(canvas, w, h);
    ctx.clearRect(0, 0, w, h);

    const maxAge = Math.max(...data.map(d => d.age), 35) * (clipPct / 100);
    const maxYrs = Math.max(...data.map(d => d.years), 20);
    const iw     = w - pad.left - pad.right;
    const ih     = h - pad.top - pad.bottom;
    const px     = age => pad.left + (age / maxAge) * iw;
    const py     = yrs => pad.top + ih - (yrs / maxYrs) * ih;

    // grid lines
    ctx.strokeStyle = '#f5f5f5';
    ctx.lineWidth   = 1;
    [5, 10, 15, 20].filter(v => v <= maxYrs).forEach(v => {
        ctx.beginPath();
        ctx.moveTo(pad.left, py(v));
        ctx.lineTo(pad.left + iw, py(v));
        ctx.stroke();
    });

    // dots + hit areas
    const dots = [];
    data.forEach(({ age, years, group, name }) => {
        const cx = px(age);
        const cy = py(years);
        ctx.beginPath();
        ctx.arc(cx, cy, 3, 0, 2 * Math.PI);
        ctx.fillStyle = STAGE_COLOR[stageOf(group)] ?? 'rgba(163,163,163,0.5)';
        ctx.fill();
        dots.push({ cx, cy, age, years, name });
    });

    // axis ticks
    ctx.fillStyle    = '#a3a3a3';
    ctx.font         = '9px monospace';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'top';
    [10, 15, 20, 25, 30, 35].filter(v => v <= maxAge).forEach(v => {
        ctx.fillText(v, px(v), h - pad.bottom + 4);
    });
    ctx.textAlign    = 'right';
    ctx.textBaseline = 'middle';
    [5, 10, 15, 20].filter(v => v <= maxYrs).forEach(v => {
        ctx.fillText(v, pad.left - 4, py(v));
    });

    // axis labels
    ctx.fillStyle    = '#d4d4d4';
    ctx.font         = '8px monospace';
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'bottom';
    ctx.fillText('age', pad.left + iw / 2, h - 1);
    ctx.save();
    ctx.translate(7, pad.top + ih / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textBaseline = 'top';
    ctx.fillText('yrs', 0, 0);
    ctx.restore();

    if (!tooltip) return;
    if (canvas._scatterMM) canvas.removeEventListener('mousemove',  canvas._scatterMM);
    if (canvas._scatterML) canvas.removeEventListener('mouseleave', canvas._scatterML);
    canvas._scatterMM = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mx   = e.clientX - rect.left;
        const my   = e.clientY - rect.top;
        const hit  = dots.find(d => Math.hypot(d.cx - mx, d.cy - my) <= 6);
        if (hit) {
            tooltip.textContent   = `${hit.age}y · ${hit.years}yrs sk`;
            tooltip.style.display = 'block';
            const _tw1 = tooltip.offsetWidth;
            tooltip.style.left    = (mx + 10 + _tw1 > canvas.parentElement.clientWidth ? mx - _tw1 - 10 : mx + 10) + 'px';
            tooltip.style.top     = (my - 24) + 'px';
        } else {
            tooltip.style.display = 'none';
        }
    };
    canvas._scatterML = () => { tooltip.style.display = 'none'; };
    canvas.addEventListener('mousemove',  canvas._scatterMM);
    canvas.addEventListener('mouseleave', canvas._scatterML);
}
